import LiquidText from '../components/LiquidText';
import { OrcamentoIcon, OrcamentoFeedbackProvider, useOrcamentoFeedback } from '../components/OrcamentoUI';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useNotificacoes } from '../context/NotificacoesContext';
import './Notificacoes.css';

/* ============================================================
   NOTIFICAÇÕES
   Separador próprio para os alertas da aplicação. Não substitui os avisos que
   já existem noutros ecrãs — esses ficam onde estão, por terem contexto
   próprio. Este é o sítio onde se vê tudo de uma vez.

   Trata dos contratos a termo (quando renovam e quando chegam ao fim da última
   renovação possível) e dos documentos com validade da empresa e dos
   trabalhadores — expirados primeiro, depois os que estão a caminho disso.
   ============================================================ */

const fmtData = (v) => {
    if (!v) return '—';
    const d = new Date(v);
    return isNaN(d) ? '—' : d.toLocaleDateString('pt-PT');
};

/* O tom acompanha a urgência: passado, esta semana, este mês, mais longe. */
const grau = (dias) => {
    if (dias < 0) return 'passou';
    if (dias <= 7) return 'urgente';
    if (dias <= 30) return 'proximo';
    return 'distante';
};

/* Em que período do contrato se está, e o que vem a seguir.
   periodo_num: 1 = período inicial, 2 = 1.ª renovação, 3 = 2.ª… */
const ordinal = (n) => `${n}.ª`;
/* Os meses saem da duração declarada, e não fixos: um contrato de 90 dias diz
   "3 meses" sem ser preciso mexer aqui. */
const emMeses = (dias) => Math.max(1, Math.round((dias || 180) / 30));
const periodoAtual = (n, dias) =>
    (n <= 1 ? `1.º período contrato ${emMeses(dias)} meses` : `${ordinal(n - 1)} renovação`);
const proximaRenovacao = (n) => `${ordinal(Math.max(1, n))} renovação`;

const textoPrazo = (dias) => {
    if (dias < 0) return `terminou há ${-dias} dia${-dias === 1 ? '' : 's'}`;
    if (dias === 0) return 'termina hoje';
    if (dias === 1) return 'termina amanhã';
    return `faltam ${dias} dias`;
};

/* Estado de um documento com validade. O expirado é o caso que interessa
   destacar — é o único em que já se está em falta. */
const estadoDoc = (dias) => {
    if (dias < 0) return { etq: 'notif-etq-fim', texto: `Expirado há ${-dias} d` };
    if (dias === 0) return { etq: 'notif-etq-fim', texto: 'Expira hoje' };
    if (dias <= 7) return { etq: 'notif-etq-urgente', texto: `Expira em ${dias} d` };
    if (dias <= 30) return { etq: 'notif-etq-aviso', texto: `Expira em ${dias} d` };
    return { etq: 'notif-etq', texto: `Expira em ${dias} d` };
};

/* ------------------------------------------------------------
   Bloco de documentos com validade.
   A empresa e os trabalhadores lêem-se da mesma maneira; muda só a coluna da
   entidade, que na empresa seria a mesma em todas as linhas.
   ------------------------------------------------------------ */
const BlocoDocumentos = ({ icone, titulo, colunaEntidade, itens, horizonte, aoAbrir, abrirTitulo }) => (
    <div className="notif-bloco">
        <div className="notif-bloco-topo">
            <h2><LiquidText>{icone}</LiquidText> <LiquidText>{titulo}</LiquidText> <span>({itens.length})</span></h2>
        </div>

        {itens.length === 0 ? (
            <p className="notif-vazio">
                <OrcamentoIcon name="check" fallback="✅" /> Nenhum documento expirado ou a expirar nos próximos {horizonte} dias.
            </p>
        ) : (
            <div className="admin-table-scroll"><table className="notif-tabela standard-table">
                <colgroup>
                    {colunaEntidade ? <><col className="notif-col-meia-entidade" /><col className="notif-col-meia-entidade" /></> : <col className="notif-col-entidade" />}
                    <col className="notif-col-data" />
                    <col className="notif-col-data" />
                    <col />
                </colgroup>
                <thead>
                    <tr>
                        {colunaEntidade && <th>{colunaEntidade}</th>}
                        <th>Documento</th>
                        <th className="notif-tc">Válido até</th>
                        <th className="notif-tc">Estado</th>
                        <th aria-hidden="true"></th>
                    </tr>
                </thead>
                <tbody>
                    {itens.map((d, i) => {
                        const e = estadoDoc(d.dias);
                        return (
                            <tr key={`${d.categoria}-${d.tipo}-${d.ref_id}-${i}`}
                                className={`notif-linha notif-${grau(d.dias)}`}
                                onClick={aoAbrir} title={abrirTitulo}>
                                {colunaEntidade && (
                                    <td><strong>{d.entidade}</strong></td>
                                )}
                                <td>
                                    {d.tipo}
                                    {d.detalhe && <div className="notif-sub">{d.detalhe}</div>}
                                </td>
                                <td className="notif-tc">{fmtData(d.validade)}</td>
                                <td className="notif-tc">
                                    <span className={`notif-etq ${e.etq}`}><LiquidText>{e.texto}</LiquidText></span>
                                </td>
                                <td aria-hidden="true"></td>
                            </tr>
                        );
                    })}
                </tbody>
            </table></div>
        )}
    </div>
);

const NotificacoesContent = () => {
    const { notify } = useOrcamentoFeedback();
    const navigate = useNavigate();
    const { recarregar: recarregarNotificacoes } = useNotificacoes();
    const [dados, setDados] = useState({
        contratos: [], documentos_empresa: [], documentos_trabalhadores: [],
        total: 0, horizonte: 90,
    });
    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState('');
    const [horizonte, setHorizonte] = useState(90);

    const buscar = useCallback(async () => {
        setCarregando(true);
        setErro('');
        try {
            const r = await api.get('/notificacoes', { params: { dias: horizonte } });
            setDados(r.data || { contratos: [], documentos_empresa: [], documentos_trabalhadores: [], total: 0 });
        } catch (e) {
            setErro(e.response?.data?.error || e.message);
        } finally {
            setCarregando(false);
        }
    }, [horizonte]);

    useEffect(() => { buscar(); }, [buscar]);

    const marcarTratado = async (contrato, ativo) => {
        try {
            await api.post(`/notificacoes/contrato/${contrato.id}/tratado`,
                ativo ? { fim_periodo: contrato.fim_periodo } : { ativo: false });
            await buscar();
            // o contador da barra lateral tem de acompanhar
            recarregarNotificacoes();
        } catch (e) {
            await notify('Não foi possível guardar: ' + (e.response?.data?.error || e.message));
        }
    };

    // Recarrega ao voltar ao separador, como nos restantes ecrãs da aplicação.
    useEffect(() => {
        const aoVoltar = () => { if (document.visibilityState === 'visible') buscar(); };
        document.addEventListener('visibilitychange', aoVoltar);
        window.addEventListener('focus', aoVoltar);
        return () => {
            document.removeEventListener('visibilitychange', aoVoltar);
            window.removeEventListener('focus', aoVoltar);
        };
    }, [buscar]);

    const contratos = dados.contratos || [];
    const docsEmpresa = dados.documentos_empresa || [];
    const docsTrabalhadores = dados.documentos_trabalhadores || [];

    return (
        <div className="notif-page admin-theme-page">
            <div className="notif-header admin-page-header">
                <div>
                    <h1><OrcamentoIcon name="bell" fallback="🔔" /> Notificações</h1>
                    <p>Alertas da aplicação reunidos num só lugar.</p>
                </div>
                <div className="notif-filtros">
                    <label>
                        Horizonte
                        <select value={horizonte} onChange={e => setHorizonte(Number(e.target.value))}>
                            <option value={30}>30 dias</option>
                            <option value={60}>60 dias</option>
                            <option value={90}>90 dias</option>
                            <option value={180}>180 dias</option>
                            <option value={365}>1 ano</option>
                        </select>
                    </label>
                    <button className="notif-btn-ref" onClick={buscar} title="Atualizar"><OrcamentoIcon name="refresh" fallback="↻" /></button>
                </div>
            </div>

            {erro && <div className="notif-erro">Erro ao consultar: {erro}</div>}
            {carregando && <p className="notif-info">A procurar…</p>}

            {!carregando && !erro && (
              <>
                <div className="notif-bloco">
                    <div className="notif-bloco-topo">
                        <h2><OrcamentoIcon name="document" fallback="📄" /> Contratos a termo <span>({contratos.length})</span></h2>
                    </div>

                    {contratos.length === 0 ? (
                        <p className="notif-vazio">
                            <OrcamentoIcon name="check" fallback="✅" /> Nenhum contrato a terminar nos próximos {dados.horizonte} dias.
                        </p>
                    ) : (
                        <div className="admin-table-scroll"><table className="notif-tabela standard-table">
                            <colgroup>
                                <col className="notif-col-entidade" />
                                <col className="notif-col-data" />
                                <col className="notif-col-data" />
                                <col className="notif-col-data" />
                                <col className="notif-col-comunicacao" />
                                <col />
                            </colgroup>
                            <thead>
                                <tr>
                                    {/* Títulos como etiqueta, no mesmo desenho das etiquetas de
                                        estado da tabela. Em duas linhas onde o título é longo: a
                                        tabela tem seis colunas e os títulos alargavam-nas mais do
                                        que os próprios dados. */}
                                    <th><span className="notif-th">Trabalhador</span></th>
                                    <th className="notif-tc"><span className="notif-th">Data<i>contrato</i></span></th>
                                    <th className="notif-tc"><span className="notif-th">Termo<i>atual</i></span></th>
                                    <th className="notif-tc"><span className="notif-th">Data<i>renovação</i></span></th>
                                    <th className="notif-tc"><span className="notif-th">Comunicar não<i>renovação até</i></span></th>
                                    <th className="notif-tc"><span className="notif-th">Estado</span></th>
                                </tr>
                            </thead>
                            <tbody>
                                {contratos.map(c => (
                                    <tr key={c.id} className={`notif-linha notif-${grau(c.dias_para_fim)}`}
                                        onClick={() => navigate('/trabalhadores')}
                                        title="Abrir Trabalhadores">
                                        <td>{c.nome}</td>
                                        <td className="notif-tc">{fmtData(c.contrato_data)}</td>
                                        <td className="notif-tc">
                                            {fmtData(c.fim_periodo)}
                                            {/* Sem data de fim na ficha, o período é deduzido da data do
                                                contrato e da duração. A origem fica no title da célula, em
                                                vez de ocupar espaço na linha. */}
                                            <div className="notif-sub"
                                                title={c.fim_calculado
                                                    ? `Calculado a partir da data do contrato e de ${c.duracao_dias} dias de duração${c.duracao_assumida ? ' (assumida)' : ''}.`
                                                    : undefined}>
                                                {textoPrazo(c.dias_para_fim)}
                                            </div>
                                        </td>
                                        <td className="notif-tc">
                                            {fmtData(c.data_renovacao)}
                                            <div className="notif-sub">
                                                {c.ultima_renovacao
                                                    ? 'última, passa a efetivo'
                                                    : `seria a ${proximaRenovacao(c.periodo_num)}`}
                                            </div>
                                        </td>
                                        {/* Dentro dos 15 dias que antecedem o prazo, a data pisca:
                                            é o único momento em que ainda se pode decidir. */}
                                        <td className="notif-tc">
                                            {/* Botão ao lado da data e não por baixo: a coluna já tem
                                                duas linhas e uma terceira alteava toda a tabela. */}
                                            <div className="notif-limite-linha">
                                                <span className={c.insistente ? 'notif-limite notif-pisca' : 'notif-limite-calmo'}>
                                                    {fmtData(c.data_comunicacao)}
                                                </span>
                                                {/* Calar o aviso é por termo, não para sempre: na renovação
                                                    seguinte há novo prazo e o aviso volta sozinho. */}
                                                {c.insistente && (
                                                    <button type="button" className="notif-btn-tratado"
                                                        onClick={e => { e.stopPropagation(); marcarTratado(c, true); }}
                                                        title="Já tratei — deixa de piscar até à próxima renovação">
                                                        <OrcamentoIcon name="check" fallback="✔" /> já tratei
                                                    </button>
                                                )}
                                                {c.tratado && (
                                                    <button type="button" className="notif-btn-repor"
                                                        onClick={e => { e.stopPropagation(); marcarTratado(c, false); }}
                                                        title="Voltar a avisar deste prazo">
                                                        <OrcamentoIcon name="back" fallback="↩" /> avisar
                                                    </button>
                                                )}
                                            </div>
                                            <div className="notif-sub">
                                                {c.dias_para_comunicacao < 0
                                                    ? `prazo terminou há ${-c.dias_para_comunicacao} dia${c.dias_para_comunicacao === -1 ? '' : 's'}`
                                                    : c.dias_para_comunicacao === 0
                                                        ? 'último dia'
                                                        : `falta${c.dias_para_comunicacao === 1 ? '' : 'm'} ${c.dias_para_comunicacao} dia${c.dias_para_comunicacao === 1 ? '' : 's'}`}
                                            </div>
                                        </td>
                                        <td className="notif-tc">
                                            {c.ultima_renovacao
                                                ? <span className="notif-etq notif-etq-fim">Última renovação</span>
                                                : <span className="notif-etq notif-etq-renovavel">
                                                    Renovável por mais {emMeses(c.duracao_dias)} meses
                                                  </span>}
                                            <div className="notif-sub">
                                                {c.ultima_renovacao
                                                    ? 'Passa a efetivo'
                                                    : periodoAtual(c.periodo_num, c.duracao_dias)}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table></div>
                    )}
                </div>

                {/* Certidões e seguros da empresa. Editam-se no Dashboard, em
                    "Dados Empresa" — é para lá que a linha leva. */}
                <BlocoDocumentos
                    icone={<OrcamentoIcon name="building" fallback="🏛️" />}
                    titulo="Documentos da empresa"
                    itens={docsEmpresa}
                    horizonte={dados.horizonte || horizonte}
                    aoAbrir={() => navigate('/', { state: { abrirDadosEmpresa: true } })}
                    abrirTitulo="Abrir os dados da empresa"
                />

                <BlocoDocumentos
                    icone={<OrcamentoIcon name="users" fallback="👷" />}
                    titulo="Documentos dos trabalhadores"
                    colunaEntidade="Trabalhador"
                    itens={docsTrabalhadores}
                    horizonte={dados.horizonte || horizonte}
                    aoAbrir={() => navigate('/trabalhadores')}
                    abrirTitulo="Abrir Trabalhadores"
                />
              </>
            )}
        </div>
    );
};

export default function Notificacoes() { return <OrcamentoFeedbackProvider><NotificacoesContent /></OrcamentoFeedbackProvider>; }
